import React, { useState, useRef, useEffect, useCallback } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import DownloadIcon from '@mui/icons-material/Download';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FolderIcon from '@mui/icons-material/Folder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CookieManager from '../../services/CookieManager';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import LicenseBar from '../../components/Admin/licenseBar';
import API from '../../services/api';
import { toast } from 'react-toastify';
import { ErrorToast } from '../../pure-components/CustomToast';

// Add the getAdminList function
const getAdminList = async () => {
  const xAuth = CookieManager.getCookie("x-auth");
  const xAccessToken = CookieManager.getCookie("x-access-token");

  let url = process.env.REACT_APP_BASE_URL + "api/admin/list/";
  let response = await fetch(url, {
    method: "get",
    headers: {
      "Content-Type": "application/json",
      "x-auth": xAuth,
      "x-access-token": xAccessToken,
      'Cache-Control': 'no-cache',
    },
  });
  return response.json();
};

const Dashboard = ({ activeSection, setActiveSection }) => {
  // console.log('Dashboard rendered, props:', { activeSection }); // Debug log to track re-renders
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [licenseCount, setLicenseCount] = useState(null);
  const [isLoadingLicense, setIsLoadingLicense] = useState(true);
  const [totalDownloads, setTotalDownloads] = useState(null);
  const [userCounts, setUserCounts] = useState({ total: 0, solo: 0, pro: 0, free: 0, corporate: 0 });
  const [download, setDownload] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [suspendedCount, setSuspendedCount] = useState(0);
  // Add new state for admin license count
  const [adminLicenseCount, setAdminLicenseCount] = useState(null);
  const [isLoadingAdminLicense, setIsLoadingAdminLicense] = useState(true);
  const profileRef = useRef(null);
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const [users, setUsers] = useState({});
  // Add states for sync license and project counts
  const [syncLicenseCount, setSyncLicenseCount] = useState(null);
  const [isLoadingSyncLicense, setIsLoadingSyncLicense] = useState(true);
  const [projectCount, setProjectCount] = useState(null);
  const [isLoadingProject, setIsLoadingProject] = useState(true);
  // Add state for super admin total projects
  const [superAdminProjectCount, setSuperAdminProjectCount] = useState(null);
  const [isLoadingSuperAdminProject, setIsLoadingSuperAdminProject] = useState(true);

  // Profile data - memoize to prevent re-calculations
  const profileData = React.useMemo(() => {
    const userData = CookieManager.getCookie('user-data');
    return userData?.length ? JSON.parse(userData) : null;
  }, []);

  const isSuperAdmin = profileData?.userType === 'SUPER_ADMIN';
  const isCorporate = profileData?.userType === 'CORPORATE';
  const isAdmin = profileData?.userType === 'ADMIN';

  // Handle outside click for dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sign out logic
  const handleSignOut = () => {
    CookieManager.deleteCookie('x-access-token');
    CookieManager.deleteCookie('user-data');
    localStorage.clear();
    window.location.href = '/signin';
  };

  // Retry logic for API calls
  const fetchWithRetry = async (fn, maxRetries = 3, delay = 1000) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await fn();
      } catch (err) {
        if (i === maxRetries - 1) throw err;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };

  // Fetch total projects for Super Admin (FIXED VERSION)
  useEffect(() => {
    const fetchSuperAdminProjects = async () => {
      if (!isSuperAdmin) return;
      
      setIsLoadingSuperAdminProject(true);
      try {
        // console.log('Fetching super admin projects...');
        
        // Try multiple approaches for fetching projects
        let response;
        
        // Approach 1: Empty object
        try {
          response = await API.loadProjects({});
          // console.log('Approach 1 - Empty object response:', response);
        } catch (err1) {
          // console.log('Approach 1 failed:', err1.message);
          
          // Approach 2: No parameters
          try {
            response = await API.loadProjects();
            // console.log('Approach 2 - No params response:', response);
          } catch (err2) {
            // console.log('Approach 2 failed:', err2.message);
            
            // Approach 3: Empty query
            try {
              response = await API.loadProjects({ query: {} });
              // console.log('Approach 3 - Empty query response:', response);
            } catch (err3) {
              // console.log('Approach 3 failed:', err3.message);
              throw err3; // Re-throw the last error
            }
          }
        }
        
        if (response) {
          // console.log('Final Super Admin Projects Response:', JSON.stringify(response, null, 2));
          
          // FIXED: Properly handle the response structure
          // Priority: response.count -> response.data.length -> 0
          let projectCount = response?.count ?? (Array.isArray(response?.data) ? response.data.length : 0);
          
          // Ensure we have a valid number
          projectCount = Number.isFinite(projectCount) ? projectCount : 0;
          
          // console.log('Final Super Admin Project Count:', projectCount);
          setSuperAdminProjectCount(projectCount);
        } else {
          // console.log('No response received');
          setSuperAdminProjectCount(0);
        }
      } catch (err) {
        console.error('Error fetching super admin project count:', {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data,
        });
        setSuperAdminProjectCount('N/A');
        toast(({ closeToast }) => <ErrorToast message={`Super Admin Projects Error: ${err.response?.data?.message || err.message}`} />);
      } finally {
        setIsLoadingSuperAdminProject(false);
      }
    };

    fetchSuperAdminProjects();
  }, [isSuperAdmin]); // Dependency: isSuperAdmin

  // Fetch admin license count for ADMIN users
  useEffect(() => {
    const fetchAdminLicenseCount = async () => {
      if (!isAdmin || !profileData?.email) return;
      
      setIsLoadingAdminLicense(true);
      try {
        const response = await getAdminList();
        
        // Check different possible response structures
        let adminList = [];
        if (response && response.data && Array.isArray(response.data)) {
          adminList = response.data;
        } else if (response && Array.isArray(response)) {
          adminList = response;
        } else {
          setAdminLicenseCount('N/A');
          return;
        }
        
        // Find the admin user by matching email
        const adminUser = adminList.find(admin => admin.email === profileData.email);
        
        if (adminUser) {
          setAdminLicenseCount(adminUser.totalLicenseCount || 0);
        } else {
          setAdminLicenseCount(0);
        }
      } catch (err) {
        console.error('Error fetching admin license count:', {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data,
        });
        setAdminLicenseCount('N/A');
        toast(({ closeToast }) => <ErrorToast message={`Admin License Count Error: ${err.message}`} />);
      } finally {
        setIsLoadingAdminLicense(false);
      }
    };

    fetchAdminLicenseCount();
  }, [isAdmin, profileData?.email]); // Dependencies: isAdmin and email

  // Fetch license count using getTotalLicenseCount - Fixed with proper dependency
  useEffect(() => {
    const fetchLicenseCount = async () => {
      setIsLoadingLicense(true);
      try {
        // console.log('Fetching license count with tokens:', {
        //   xAuth: CookieManager.getCookie('x-auth'),
        //   xAccessToken: CookieManager.getCookie('x-access-token'),
        //   url: process.env.REACT_APP_BASE_URL + 'api/license/count',
        // });
        const data = await fetchWithRetry(() => API.getTotalLicenseCount());
        // console.log('License count response:', JSON.stringify(data, null, 2));
        setLicenseCount(data?.data?.licenseCount || data?.licenseCount || data?.count || 0);
      } catch (err) {
        console.error('Error fetching license count:', {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data,
          url: process.env.REACT_APP_BASE_URL + 'api/license/count',
        });
        setLicenseCount('N/A');
        toast(({ closeToast }) => <ErrorToast message={`License Count Error: ${err.response?.data?.message || err.message}`} />);
      } finally {
        setIsLoadingLicense(false);
      }
    };

    // Fetch license count on component mount
    fetchLicenseCount();
  }, []); // Empty dependency array - run only once

  // Fetch total downloads - Fixed with proper cleanup and single execution
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchTotalDownloads = async () => {
      const requestId = Date.now();
      // console.log(`Fetching total downloads (ID: ${requestId})`);
      
      try {
        const query = [
          { $project: { lable: 'count', downloadedTracks: { $sum: { $size: '$downloadedTracks' } } } },
          { $group: { _id: '$lable', total: { $sum: '$downloadedTracks' } } },
        ];
        
        const response = await API.getUserAggregate(query, { signal: controller.signal });
        // console.log(`Total downloads response (ID: ${requestId}):`, JSON.stringify(response, null, 2));
        
        if (isMounted) {
          setTotalDownloads(Array.isArray(response.data) && response.data.length > 0 ? response.data[0].total : 0);
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          // console.log(`Total downloads request aborted (ID: ${requestId})`);
          return;
        }
        console.error(`Error fetching downloads (ID: ${requestId}):`, {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data,
          url: process.env.REACT_APP_BASE_URL + 'api/users/aggregate',
        });
        if (isMounted) {
          setTotalDownloads('N/A');
          toast(({ closeToast }) => <ErrorToast message={`Downloads Error: ${err.response?.data?.message || err.message}`} />);
        }
      }
    };

    // Only fetch if not already loaded
    if (totalDownloads === null) {
      fetchTotalDownloads();
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []); // Empty dependency array - run only once

  // Fetch user counts by type - Fixed with proper memoization
  useEffect(() => {
    let isMounted = true;

    const fetchUserCounts = async () => {
      try {
        let query = [];
        if (isSuperAdmin) {
          query = [
            {
              $group: {
                _id: '$userType',
                total: { $sum: 1 },
              },
            },
          ];
        } else {
          query = [
            {
              $match: {
                userType: 'CORPORATE',
                admin: profileData?.email,
              },
            },
            {
              $group: {
                _id: null,
                total: { $sum: 1 },
              },
            },
          ];
        }

        const response = await API.getUserAggregate(query);
        
        if (!isMounted) return; // Prevent state update if component unmounted
        
        if (response.success && Array.isArray(response.data)) {
          if (isSuperAdmin) {
            const counts = {
              total: 0,
              solo: 0,
              pro: 0,
              free: 0,
              corporate: 0,
            };
            response.data.forEach(item => {
              const type = item._id?.toLowerCase();
              if (type === 'solo') counts.solo = item.total;
              if (type === 'pro') counts.pro = item.total;
              if (type === 'free') counts.free = item.total;
              if (type === 'corporate') counts.corporate = item.total;
              counts.total += item.total;
            });
            setUserCounts(counts);
          } else {
            const total = response.data.length > 0 ? response.data[0].total : 0;
            setUserCounts({ total, solo: 0, pro: 0, free: 0, corporate: total });
          }
        } else {
          setUserCounts({ total: 0, solo: 0, pro: 0, free: 0, corporate: 0 });
        }
      } catch (err) {
        console.error('Error fetching user counts:', err);
        if (isMounted) {
          setUserCounts({ total: 'N/A', solo: 'N/A', pro: 'N/A', free: 'N/A', corporate: 'N/A' });
          toast(({ closeToast }) => <ErrorToast message={`User Counts Error: ${err.response?.data?.message || err.message}`} />);
        }
      }
    };

    if (profileData?.email && userCounts.total === 0) {
      fetchUserCounts();
    }

    return () => {
      isMounted = false;
    };
  }, [profileData?.email, profileData?.userType, isSuperAdmin]); // Stable dependencies

  // Chart data for users by month - Fixed with proper cleanup
  useEffect(() => {
    let isMounted = true;

    const fetchChartData = async () => {
      const query = [
        {
          $project: {
            month: { $month: '$createdAt' },
            year: { $year: '$createdAt' },
          },
        },
        {
          $group: {
            _id: { month: '$month', year: '$year' },
            total: { $sum: 1 },
            month: { $first: '$month' },
            year: { $first: '$year' },
          },
        },
        {
          $match: {
            year: new Date().getFullYear(),
          },
        },
      ];
      
      try {
        const response = await API.getUserAggregate(query);
        if (isMounted && response.success) {
          chartData(response.data);
        }
      } catch (err) {
        console.error('Error fetching chart data:', err);
        if (isMounted) {
          toast(({ closeToast }) => <ErrorToast message={`Chart Data Error: ${err.response?.data?.message || err.message}`} />);
        }
      }
    };

    // Only fetch if users chart is not already populated
    if (Object.keys(users).length === 0) {
      fetchChartData();
    }

    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - run only once

  const chartData = (filterdata = []) => {
    if (filterdata.length) {
      filterdata = filterdata.sort((a, b) => (a.month > b.month) ? 1 : -1);
      let xaxis = month.slice(0, filterdata[filterdata.length - 1].month);
      let ydata = [];
      let i = 1;
      while (i <= filterdata[filterdata.length - 1].month) {
        let filtermonth = filterdata.filter(item => item.month === i);
        ydata[i - 1] = filtermonth.length ? filtermonth[0].total : 0;
        i++;
      }
      let chartopt = {
        chart: {
          type: 'line',
          height: '350px',
        },
        title: {
          text: 'Total number of users',
        },
        plotOptions: {
          line: {
            dataLabels: {
              enabled: true,
            },
            enableMouseTracking: false,
          },
        },
        xAxis: {
          categories: xaxis,
        },
        yAxis: {
          title: {
            text: 'No. of Users',
          },
        },
        series: [
          {
            name: 'month',
            data: ydata,
          },
        ],
      };
      setUsers(chartopt);
    }
  };

  useEffect(() => {
    const profileData = CookieManager.getCookie('user-data')?.length
      ? JSON.parse(CookieManager.getCookie('user-data'))
      : null;
    if (profileData?.userType === 'CORPORATE' || profileData?.userType === 'ADMIN') {
      let filter = { userType: 'CORPORATE' };
      if (profileData?.userType === 'ADMIN') filter['admin'] = profileData.email;
      API.getUserList({ filter }).then(res => {
        if (res.success && Array.isArray(res.data)) {
          setActiveCount(res.data.filter(u => !u.isSuspended).length);
          setSuspendedCount(res.data.filter(u => u.isSuspended).length);

          // Fetch sync license count, project count, and downloads for admin
          if (profileData?.userType === 'ADMIN') {
            const userIds = res.data.map(u => u._id);

            // Fetch sync licenses
            setIsLoadingSyncLicense(true);
            const licenseQuery = { licensee: { '$in': userIds } };
            API.getLicenses({ query: licenseQuery }).then(resp => {
              if (resp.success) {
                setSyncLicenseCount(resp.count || resp.data?.length || 0);
              } else {
                setSyncLicenseCount('N/A');
                toast(({ closeToast }) => <ErrorToast message={`License Count Error: ${resp?.msg || 'Unknown error'}`} />);
              }
              setIsLoadingSyncLicense(false);
            }).catch(err => {
              console.error('Error fetching sync licenses:', err);
              setSyncLicenseCount('N/A');
              setIsLoadingSyncLicense(false);
              toast(({ closeToast }) => <ErrorToast message={`Sync Licenses Error: ${err.response?.data?.message || err.message}`} />);
            });

            // Fetch project count
            setIsLoadingProject(true);
            const projectQuery = { userId: { '$in': userIds } };
            API.loadProjects({ query: projectQuery }).then(resp => {
              if (resp.success) {
                setProjectCount(resp.count || resp.data?.length || 0);
              } else {
                setProjectCount('N/A');
                toast(({ closeToast }) => <ErrorToast message={`Project Count Error: ${resp?.msg || 'Unknown error'}`} />);
              }
              setIsLoadingProject(false);
            }).catch(err => {
              console.error('Error fetching projects:', err);
              setProjectCount('N/A');
              setIsLoadingProject(false);
              toast(({ closeToast }) => <ErrorToast message={`Projects Error: ${err.response?.data?.message || err.message}`} />);
            });

            // Fetch downloads
            const downloadQuery = [
              { $match: { _id: { '$in': userIds } } },
              { $project: { lable: 'count', downloadedTracks: { $size: '$downloadedTracks' } } },
              { $group: { _id: '$lable', total: { $sum: '$downloadedTracks' } } },
            ];
            API.getUserAggregate(downloadQuery).then(response => {
              if (response.success) {
                setTotalDownloads(Array.isArray(response.data) && response.data.length > 0 ? response.data[0].total : 0);
              } else {
                setTotalDownloads('N/A');
              }
            }).catch(err => {
              console.error('Error fetching downloads for admin:', err);
              setTotalDownloads('N/A');
              toast(({ closeToast }) => <ErrorToast message={`Downloads Error: ${err.response?.data?.message || err.message}`} />);
            });
          }
        }
      });
    }
  }, []);

  return (
    <div className="p-4">
      {/* High Level Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 mb-8">
        {/* Total Users - Always visible */}
        <div className="bg-white p-5 rounded-lg shadow flex items-center gap-4 hover:scale-105 transition-transform">
          <PersonIcon className="text-blue-600" style={{ fontSize: 40 }} />
          <div>
            <h3 className="text-sm text-gray-500 font-medium">Total Users</h3>
            <p className="text-2xl font-bold text-gray-800">
              {userCounts.total === 'N/A' ? 'N/A' : userCounts.total}
            </p>
          </div>
        </div>

        {(isCorporate || profileData?.userType === 'ADMIN') && (
          <div className="bg-white p-5 rounded-lg shadow flex items-center gap-4 hover:scale-105 transition-transform">
            <AssignmentIcon className="text-purple-600" style={{ fontSize: 40 }} />
            <div>
              <h3 className="text-sm text-gray-500 font-medium">Sync Licenses</h3>
              <p className="text-2xl font-bold text-gray-800">{isLoadingSyncLicense ? 'Loading...' : (syncLicenseCount === null ? 'N/A' : syncLicenseCount)}</p>
            </div>
          </div>
        )}

        {(isCorporate || profileData?.userType === 'ADMIN') && (
          <div className="bg-white p-5 rounded-lg shadow flex items-center gap-4 hover:scale-105 transition-transform">
            <FolderIcon className="text-yellow-500" style={{ fontSize: 40 }} />
            <div>
              <h3 className="text-sm text-gray-500 font-medium">Total Projects</h3>
              <p className="text-2xl font-bold text-gray-800">{isLoadingProject ? 'Loading...' : (projectCount === null ? 'N/A' : projectCount)}</p>
            </div>
          </div>
        )}

        {/* Total Downloads - Show only for non-Corporate/Admin users */}
        {!(isCorporate || profileData?.userType === 'ADMIN') && (
          <div className="bg-white p-5 rounded-lg shadow flex items-center gap-4 hover:scale-105 transition-transform">
            <DownloadIcon className="text-green-600" style={{ fontSize: 40 }} />
            <div>
              <h3 className="text-sm text-gray-500 font-medium">Total Downloads</h3>
              <p className="text-2xl font-bold text-gray-800">{totalDownloads === null ? 'Loading...' : totalDownloads}</p>
            </div>
          </div>
        )}

        {/* {(isCorporate || profileData?.userType === 'ADMIN') && (
          <div className="bg-white p-5 rounded-lg shadow flex items-center gap-4 hover:scale-105 transition-transform">
            <DownloadIcon className="text-green-600" style={{ fontSize: 40 }} />
            <div>
              <h3 className="text-sm text-gray-500 font-medium">Total Downloads</h3>
              <p className="text-2xl font-bold text-gray-800">-</p>
              <p className="text-2xl font-bold text-gray-800">{totalDownloads === null ? 'Loading...' : totalDownloads}</p>
            </div>
          </div>
        )} */}

        {/* Total Licenses - Always visible */}
        {!(isCorporate || profileData?.userType === 'ADMIN') && (
          <div className="bg-white p-5 rounded-lg shadow flex items-center gap-4 hover:scale-105 transition-transform">
            <AssignmentIcon className="text-purple-600" style={{ fontSize: 40 }} />
            <div>
              <h3 className="text-sm text-gray-500 font-medium">Total Licenses</h3>
              <p className="text-2xl font-bold text-gray-800">{isLoadingLicense ? 'Loading...' : (licenseCount === null ? 'N/A' : licenseCount)}</p>
            </div>
          </div>
        )}

        {/* Total Projects - Show for Super Admin and non-Corporate/Admin users */}
        {!(isCorporate || profileData?.userType === 'ADMIN') && (
          <div className="bg-white p-5 rounded-lg shadow flex items-center gap-4 hover:scale-105 transition-transform">
            <FolderIcon className="text-yellow-500" style={{ fontSize: 40 }} />
            <div>
              <h3 className="text-sm text-gray-500 font-medium">Total Projects</h3>
              <p className="text-2xl font-bold text-gray-800">
                {isSuperAdmin ? 
                  (isLoadingSuperAdminProject ? 'Loading...' : superAdminProjectCount) : 
                  '-'
                }
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Corporate Users List and Add User Option for Corporate */}
      {isCorporate && (
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-blue-600">Corporate Users</h2>
            <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">Add User</button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg shadow text-sm">
              <thead>
                <tr>
                  <th className="bg-[#f7f8fa] text-gray-700 font-semibold px-4 py-3 text-left">Name</th>
                  <th className="bg-[#f7f8fa] text-gray-700 font-semibold px-4 py-3 text-left">Email</th>
                  <th className="bg-[#f7f8fa] text-gray-700 font-semibold px-4 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={3} className="text-center py-8 text-gray-400">No users found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Insights Section */}
      <div className={`grid gap-6 ${
        isSuperAdmin && profileData?.userType === 'SUPER_ADMIN' 
          ? 'grid-cols-1 md:grid-cols-2' 
          : 'grid-cols-1 md:grid-cols-2'
      }`}>
        <div className="bg-white p-4 rounded-lg shadow min-w-[300px] mb-4"><LicenseBar /></div>
        {profileData?.userType == 'ADMIN' && (
          <div className="bg-white p-6 rounded-lg shadow min-w-[300px] mb-4 flex flex-col gap-4">
            {/* Total Licenses Card - Updated to show actual admin license count */}
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-xl shadow-sm  hover:shadow-md transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-600 rounded-xl">
                  <AssignmentIcon className="text-white" style={{ fontSize: 40 }} />
                </div>
                <div>
                  <h3 className="text-sm text-gray-600 font-semibold uppercase tracking-wide">Total Licenses</h3>
                  <p className="text-3xl font-bold text-gray-800">
                    {isLoadingAdminLicense ? 'Loading...' : (adminLicenseCount === null ? 'N/A' : adminLicenseCount)}
                  </p>
                </div>
              </div>
            </div>

            {/* Active Users Card */}
            <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-xl  shadow-sm  flex-1 hover:shadow-md transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-600 rounded-xl">
                  <PersonIcon className="text-white" style={{ fontSize: 40 }} />
                </div>
                <div>
                  <h3 className="text-sm text-gray-600 font-semibold uppercase tracking-wide">Active Users</h3>
                  <p className="text-3xl font-bold text-gray-800">{activeCount}</p>
                </div>
              </div>
            </div>

            {/* Suspended Users Card */}
            <div className="bg-gradient-to-r from-red-50 to-red-100 p-6 rounded-xl shadow-sm  flex-1 hover:shadow-md transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-600 rounded-xl">
                  <PersonIcon className="text-white" style={{ fontSize: 40 }} />
                </div>
                <div>
                  <h3 className="text-sm text-gray-600 font-semibold uppercase tracking-wide">Suspended Users</h3>
                  <p className="text-3xl font-bold text-gray-800">{suspendedCount}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Chart only show when super admin login */}
        {isSuperAdmin && profileData?.userType === 'SUPER_ADMIN' && (  
          <div className="bg-white p-4 rounded-lg shadow min-w-[300px] mb-4"><HighchartsReact highcharts={Highcharts} options={users} /></div>
        )}
      </div>
    </div>
  );
};

export default React.memo(Dashboard);
